/**
 * Cookie consent (UK PECR / GDPR) and the analytics it gates — TASK-1162.
 *
 * The window is InMobi Choice, a Google-certified IAB TCF platform
 * (`inMobiChoice.ts`), shown to every visitor, worldwide (owner's decision
 * 2026-11-03: one window everywhere, in the site's design; the platform's
 * "GDPR for users in the US region" is on). Its answer arrives through
 * `__tcfapi` and is read as two flags (`tcfConsent.ts`):
 *
 * - **analytics** → GA4 Consent Mode `analytics_storage`, and Amplitude in
 *   the consented mode (cookies, a visitor across visits). Without it GA4
 *   sends cookieless pings and Amplitude runs `cookieless` on the server's
 *   per-day visitor id (`amplitudeInitOptions`).
 * - **advertising** → GA4 Consent Mode `ad_storage`, `ad_user_data`,
 *   `ad_personalization`. AdSense reads the TC string from `__tcfapi` itself.
 *
 * The analytics side runs only where the city has web analytics
 * (`CITY.webAnalytics`); the window runs in every city, because
 * advertising needs it too. Production-host + automation/bot gating stays
 * inside each analytics loader, so localhost dev and Playwright runs do not
 * reach the analytics properties even after consent.
 */
import { CITY } from '@city'
import { isProductionEnvironment } from '../analytics/environment'
import { recordWebLaunch } from '../analytics/applicationLaunchEvents'
import { recordWebDeeplinkOnce } from '../analytics/deeplinkEntry'
import { reportHealthError, HEALTH_ID } from '../analytics/appHealthEvents'
import { amplitudeInitOptions, type AmplitudeIdentity } from '../analytics/amplitudeInitOptions'
import { fetchVisitorDeviceId } from '../analytics/fetchVisitorDeviceId'
import { browserTimeZonePlugin } from '../analytics/browserTimeZonePlugin'
import { consentModePlugin } from '../analytics/consentModePlugin'
import { recordConsentChoice } from '../analytics/consentChoiceEvents'
import { reportAnalyticsEvent } from '../analytics/webAnalyticsReporter'
import { installAmplitudeCommandQueue } from '../analytics/installAmplitudeCommandQueue'
import { brandConsentWindow, loadInMobiChoice, onTcfEvent } from './inMobiChoice'
import { localizeConsentWindow } from './consentWindowLabels'
import { i18n } from '../i18n/config'
import { consentFromTcData, decisionFromTcData, type TcfConsent, type TcData } from './tcfConsent'
import './inMobiChoiceTheme.css'

// The city's GA4 stream and Amplitude project: `CITY.webAnalytics`
// (ingestion ids — safe-client rule #5); production-only emit/init keeps dev
// + bots off them.
const AMPLITUDE_SDK_URL = 'https://cdn.amplitude.com/libs/analytics-browser-2.11.1-min.js.gz'
const AMPLITUDE_SDK_INTEGRITY =
  'sha384-XxDPxKDB79eXOLF+koPFe4h9iVF6LgQ1J6Do9sdhcohvTlVMK7EJE74b1b0iK3uK'

/**
 * How long the analytics wait for the platform to report a
 * stored answer (`tcloaded`) before starting cookieless. Events emitted
 * meanwhile wait in the Amplitude command queue. A platform that never
 * loads (blocked, offline, no property for the domain) ends here too.
 */
export const CONSENT_WAIT_MS = 3_000

/**
 * `?cmp=preview` loads the window on any host (localhost included) — for
 * reviewing its look against the design. It
 * changes nothing on the analytics side: those loaders keep their own
 * production gate.
 */
const PREVIEW_PARAM = 'cmp'

let ga4Loaded = false
let amplitudeLoaded = false
let launchRecorded = false

/**
 * Fire the web `application_launch` / `application_first_launch` retention
 * emit (Gap G-8) exactly once per page load, the first time analytics is
 * allowed. Consent-gated by construction; production-gated + fail-safe
 * inside `recordWebLaunch` → `reportAnalyticsEvent`, so it never throws
 * into startup.
 */
function recordLaunchOnce(): void {
  if (launchRecorded) return
  launchRecorded = true
  recordWebLaunch()
  // G-1 `deeplink_opened` — entry-point attribution for this load, fired
  // alongside the launch event (same consent-gated, once-per-load path).
  // No-op for a direct open (bare map home). The entry URL was frozen at
  // module load, before any client-side navigation.
  recordWebDeeplinkOnce()
}

/**
 * Inject the GA4 tag in Consent Mode v2 with every signal denied — GA sets
 * no cookies and sends only cookieless pings, which keeps the traffic
 * visible (modeled) instead of the pre-consent black hole where GSC showed
 * 358 clicks and GA saw 81 sessions; `applyGoogleConsent` upgrades it on
 * the answer.
 */
export function loadGa4(): void {
  if (ga4Loaded) return
  ga4Loaded = true
  const analytics = CITY.webAnalytics
  if (!analytics || !isProductionEnvironment()) return
  const measurementId = analytics.ga4MeasurementId
  const tag = document.createElement('script')
  tag.async = true
  tag.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId
  // §3 `sdk_init_failed` (domain=sdk_init) — the GA4 tag failed to load
  // (blocked / offline / CDN down). The inline `gtag` stub below still exists
  // (it queues into dataLayer), so this health emit isn't lost; it just won't
  // flush to GA. `warning` — analytics degraded, not a user-facing failure.
  // The Amplitude script tag reports the same way, see `startAmplitude`.
  tag.onerror = () => {
    reportHealthError(HEALTH_ID.sdkInitFailed, 'GA4 gtag.js failed to load', {
      domain: 'sdk_init',
      severity: 'warning',
      errorKind: 'offline',
      fallback: 'default',
      extra: { sdk: 'ga4' },
    })
  }
  document.head.appendChild(tag)
  const inline = document.createElement('script')
  inline.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });
    gtag('js', new Date());
    gtag('config', '${measurementId}', { anonymize_ip: true });
  `
  document.head.appendChild(inline)
}

/** The answer → Consent Mode v2 on the GA4 tag already on the page. */
export function applyGoogleConsent(consent: TcfConsent): void {
  if (!isProductionEnvironment()) return
  const state = (allowed: boolean) => (allowed ? 'granted' : 'denied')
  const w = window as { gtag?: (...args: unknown[]) => void }
  try {
    w.gtag?.('consent', 'update', {
      analytics_storage: state(consent.analytics),
      ad_storage: state(consent.advertising),
      ad_user_data: state(consent.advertising),
      ad_personalization: state(consent.advertising),
    })
  } catch {
    // gtag threw — analytics never breaks the page (safe-client #2).
  }
}

/**
 * Load the Amplitude Browser SDK (cdn.amplitude.com) and `init` it as
 * `identity`. The `integrity` hash is the SHA-384 of the file as served
 * (decoded), checked against the CDN by `infra/deploy/smoke.sh`: from June
 * to October 2026 it carried a hash of some other build, the browser
 * refused the SDK, and the web sent Amplitude nothing while GA4 kept
 * receiving. Idempotent (one SDK and one mode per page load — the SDK
 * cannot change its storage after `init`, so an answer given after a
 * cookieless start applies from the next page load) + production-guarded.
 * `installAmplitudeCommandQueue` stands in for the SDK until the script
 * arrives, so the `init` and early events are replayed rather than lost.
 */
function startAmplitude(identity: AmplitudeIdentity): void {
  if (amplitudeLoaded) return
  amplitudeLoaded = true
  if (!isProductionEnvironment()) return
  installAmplitudeCommandQueue()
  const s = document.createElement('script')
  s.src = AMPLITUDE_SDK_URL
  s.integrity = AMPLITUDE_SDK_INTEGRITY
  s.crossOrigin = 'anonymous'
  s.async = true
  // §3 `sdk_init_failed` — blocked / offline / CDN down, or an integrity
  // mismatch (the browser reports a refused SRI check as a load error).
  // Reaches GA4; Amplitude is the one that is down.
  s.onerror = () => {
    reportHealthError(HEALTH_ID.sdkInitFailed, 'Amplitude SDK failed to load', {
      domain: 'sdk_init',
      severity: 'warning',
      errorKind: 'offline',
      fallback: 'default',
      extra: { sdk: 'amplitude' },
    })
  }
  document.head.appendChild(s)
  try {
    const apiKey = CITY.webAnalytics?.amplitudeApiKey
    if (!apiKey) return
    window.amplitude?.init?.(
      apiKey,
      undefined,
      amplitudeInitOptions(identity, window.location.hostname),
    )
    window.amplitude?.add?.(consentModePlugin(identity.mode))
    window.amplitude?.add?.(browserTimeZonePlugin())
  } catch {
    // Vendor queue threw — analytics never breaks the page (safe-client #2).
  }
}

/** Analytics allowed: the launch event and the consented Amplitude. */
function startConsentedAnalytics(): void {
  if (!isProductionEnvironment()) return
  recordLaunchOnce()
  startAmplitude({ mode: 'consented' })
}

/**
 * Amplitude for a visitor without consent: the SDK starts once the links
 * service has named the visitor for the day (`fetchVisitorDeviceId`).
 * Events emitted meanwhile wait in the command queue. No id (service down,
 * slow, no address) means no Amplitude on this page — an id per page load
 * would count every click as a new user again. Consent given before the id
 * arrives starts the consented SDK instead, and the late id is dropped
 * (`startAmplitude` runs once per page).
 */
function startCookielessAmplitude(): void {
  if (!isProductionEnvironment()) return
  installAmplitudeCommandQueue()
  void fetchVisitorDeviceId().then((deviceId) => {
    if (deviceId) startAmplitude({ mode: 'cookieless', deviceId })
  })
}

/** A button label from `common:consent_window`; empty until the catalog is in. */
function catalogLabel(key: string): string {
  const path = `common:consent_window.${key}`
  return i18n.exists(path) ? i18n.t(path) : ''
}

/** The page may run the consent window: the city's own domain, or a preview. */
function consentWindowPreview(): boolean {
  return new URLSearchParams(window.location.search).get(PREVIEW_PARAM) === 'preview'
}

function onCityDomain(): boolean {
  const host = window.location.hostname
  return (host === CITY.domain || host === `www.${CITY.domain}`) && !window.navigator.webdriver
}

/**
 * Start the consent window and the analytics it gates (where the city has
 * web analytics). Call once, before React mounts. Never throws.
 */
export function initCookieConsent(): void {
  if (consentWindowPreview() || onCityDomain()) {
    brandConsentWindow(CITY.appIcon, CITY.wordmark)
    localizeConsentWindow(() => ({
      acceptAll: catalogLabel('accept_all'),
      rejectAll: catalogLabel('reject_all'),
      saveMyChoices: catalogLabel('save_my_choices'),
    }))
    loadInMobiChoice(CITY.domain, () =>
      reportHealthError(HEALTH_ID.sdkInitFailed, 'Consent platform failed to load', {
        domain: 'sdk_init',
        severity: 'warning',
        errorKind: 'offline',
        fallback: 'default',
        extra: { sdk: 'inmobi_choice' },
      }),
    )
  }
  // Analytics: only where the city has them, and only in production — dev,
  // bots and previews get the window at most, never a vendor SDK.
  if (!CITY.webAnalytics || !isProductionEnvironment()) return

  loadGa4()
  installAmplitudeCommandQueue()
  // One Amplitude start per page: whichever of consent, the window opening
  // or the wait running out comes first. A consented start after a
  // cookieless one applies from the next page load (`startAmplitude`).
  let analyticsStarted = false
  // `tcloaded` reports a stored answer; without one the next answer is the
  // visitor's first, any later one a change made in "Cookie settings".
  let answered = false
  const startCookieless = () => {
    if (analyticsStarted) return
    analyticsStarted = true
    startCookielessAmplitude()
  }
  setTimeout(startCookieless, CONSENT_WAIT_MS)

  onTcfEvent((tcData: TcData) => {
    if (tcData.eventStatus === 'cmpuishown') {
      // Counted cookieless while the window is up.
      startCookieless()
      return
    }
    if (tcData.eventStatus !== 'tcloaded' && tcData.eventStatus !== 'useractioncomplete') return
    const consent = consentFromTcData(tcData)
    applyGoogleConsent(consent)
    if (consent.analytics) {
      analyticsStarted = true
      startConsentedAnalytics()
    } else {
      startCookieless()
    }
    if (tcData.eventStatus === 'useractioncomplete') {
      recordConsentChoice(reportAnalyticsEvent, decisionFromTcData(tcData), consent, !answered)
    }
    answered = true
  })
}

/* @internal — test-only reset of the loaded flags. */
export function __resetForTests(): void {
  ga4Loaded = false
  amplitudeLoaded = false
  launchRecorded = false
}
